import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useState } from "react";
import { Search, Filter } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import AdminLayout from "@/components/admin/AdminLayout";
import AdminUserCard, { type AdminUser } from "@/components/admin/AdminUserCard";

export default function AdminUsers() {
  const [search, setSearch] = useState("");
  const [sourceFilter, setSourceFilter] = useState("all");

  const { data: users = [], isLoading, error } = useQuery<AdminUser[]>({
    queryKey: ["admin-users"],
    queryFn: async () => {
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) throw new Error("Not authenticated");

      const { data, error } = await supabase.functions.invoke("admin-users");
      if (error) throw error;
      return data as AdminUser[];
    },
  });

  const INTERNAL_EMAILS = ["chang.can@gmail.com", "ewtming@gmail.com"];
  const isInternal = (u: AdminUser) => {
    const email = u.email?.toLowerCase() || "";
    return email.endsWith("@hostcaptain.co") || INTERNAL_EMAILS.includes(email);
  };

  const sortBySignup = (a: AdminUser, b: AdminUser) =>
    new Date(b.created_at).getTime() - new Date(a.created_at).getTime();

  // Collect unique lead sources for filter
  const allSources = Array.from(new Set(users.map((u) => u.lead_source).filter(Boolean) as string[])).sort();

  const filtered = users.filter((u) => {
    if (search) {
      const q = search.toLowerCase();
      if (
        !u.full_name?.toLowerCase().includes(q) &&
        !u.email?.toLowerCase().includes(q) &&
        !u.company_name?.toLowerCase().includes(q)
      )
        return false;
    }
    if (sourceFilter === "untagged") return !u.lead_source;
    if (sourceFilter !== "all") return u.lead_source === sourceFilter;
    return true;
  });

  const realUsers = filtered.filter((u) => !isInternal(u)).sort(sortBySignup);
  const internalUsers = filtered.filter(isInternal).sort(sortBySignup);

  const UserGrid = ({ items }: { items: AdminUser[] }) => (
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
      {items.map((user) => (
        <AdminUserCard key={user.user_id} user={user} />
      ))}
      {items.length === 0 && (
        <p className="col-span-full py-8 text-center text-muted-foreground">
          No users match your search.
        </p>
      )}
    </div>
  );

  return (
    <AdminLayout>
      <div className="p-6 lg:p-8">
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold">Users</h1>
            <p className="text-sm text-muted-foreground">
              {realUsers.length} real user{realUsers.length !== 1 ? "s" : ""} · {internalUsers.length} internal
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={sourceFilter} onValueChange={setSourceFilter}>
              <SelectTrigger className="h-9 w-[140px]">
                <Filter className="mr-1 h-3.5 w-3.5 text-muted-foreground" />
                <SelectValue placeholder="Source" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All sources</SelectItem>
                <SelectItem value="untagged">Untagged</SelectItem>
                {allSources.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="relative w-full max-w-xs">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search name, email, company…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>
        </div>

        {isLoading && (
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-64 rounded-lg" />
            ))}
          </div>
        )}

        {error && (
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
            Failed to load users. Please try again.
          </div>
        )}

        {!isLoading && !error && (
          <div className="space-y-10">
            <section>
              <h2 className="mb-4 text-lg font-semibold">Real Users</h2>
              <UserGrid items={realUsers} />
            </section>
            <section>
              <h2 className="mb-4 text-lg font-semibold text-muted-foreground">Internal</h2>
              <UserGrid items={internalUsers} />
            </section>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
